/**
 * voiceTabsStore — vibe-designer 1.163.0 voice tabs (WS-A).
 *
 * Per-tenant zustand store for the voice tab strip. Mirrors chatTabsStore
 * shape with two adaptations per worldview §3.4:
 *   1. No IndexedDB reconciliation — voice has no equivalent of
 *      getAppStorage().sessions.
 *   2. closeTab is purely local removal of tab metadata + delegates to
 *      voiceStore.dropTab to release the closed tab's byTabId entry.
 *
 * Tab metadata (id, label, createdAt) persists to localStorage
 * `vibe_voice_tabs_${tenantId}` so the tab set survives a refresh.
 * Per-tab VoiceSettings live in a separate schema-v2 key handled by
 * voiceStore (D-A2). 1.167.0 — per-tab voice chat history ALSO persists,
 * keyed by tabId in `vibe_voice_history_v2_${tenantId}`, written by a
 * voiceStore subscriber. D-A5's "transcripts in-memory only" rule was
 * reversed by user directive 2026-05-20.
 *
 * MAX_VOICE_TABS = 4 — same cap as chatTabsStore (worldview §3.4).
 */
import { create } from 'zustand';
import { useVoiceStore } from './voiceStore';
import { randomUUID } from '../utils/randomUUID';

export interface VoiceTab {
  /** uuid; React key + voiceStore byTabId key. */
  id: string;
  /** User-facing title. Initially "Voice tab N"; user-editable via updateLabel. */
  label: string;
  /** epoch ms — used for stable ordering. */
  createdAt: number;
}

interface PersistedShape {
  tabs: VoiceTab[];
  activeTabId: string | null;
}

const MAX_TABS = 4;
const STORAGE_KEY_PREFIX = 'vibe_voice_tabs_';

export const VOICE_TABS_MAX = MAX_TABS;

function storageKeyFor(tenantId: string): string {
  return `${STORAGE_KEY_PREFIX}${tenantId}`;
}

function readStored(tenantId: string): PersistedShape {
  try {
    const raw = localStorage.getItem(storageKeyFor(tenantId));
    if (!raw) return { tabs: [], activeTabId: null };
    const parsed = JSON.parse(raw) as PersistedShape;
    if (!parsed || !Array.isArray(parsed.tabs)) {
      return { tabs: [], activeTabId: null };
    }
    return {
      tabs: parsed.tabs.filter(
        (t): t is VoiceTab =>
          typeof t?.id === 'string' &&
          typeof t?.label === 'string' &&
          typeof t?.createdAt === 'number',
      ),
      activeTabId: typeof parsed.activeTabId === 'string' ? parsed.activeTabId : null,
    };
  } catch {
    return { tabs: [], activeTabId: null };
  }
}

function writeStored(tenantId: string, shape: PersistedShape): void {
  try {
    localStorage.setItem(storageKeyFor(tenantId), JSON.stringify(shape));
  } catch {
    // Quota / disabled — runtime stays correct.
  }
}

interface VoiceTabsState {
  tabs: VoiceTab[];
  activeTabId: string | null;
  /** Restore from localStorage; null tenantId clears. No IndexedDB
   * reconciliation (worldview §3.4 — voice has no sessions store). */
  hydrate: (tenantId: string | null) => Promise<void>;
  /** Append + activate new tab. Returns null at MAX_VOICE_TABS. */
  newTab: (tenantId: string) => VoiceTab | null;
  /** Local removal only — no IndexedDB delete (voice has no equivalent). */
  closeTab: (tenantId: string, tabId: string) => Promise<void>;
  /** No-op on unknown id. */
  setActiveTab: (tenantId: string, tabId: string) => void;
  /** Update tab's label + persist. */
  updateLabel: (tenantId: string, tabId: string, label: string) => void;
}

export const useVoiceTabsStore = create<VoiceTabsState>((set, get) => ({
  tabs: [],
  activeTabId: null,

  hydrate: async (tenantId) => {
    if (!tenantId) {
      set({ tabs: [], activeTabId: null });
      return;
    }
    const stored = readStored(tenantId);
    const tabs: VoiceTab[] = [...stored.tabs];
    let activeTabId: string | null = stored.activeTabId;
    // Guard: stored activeTabId may reference a tab no longer in the list.
    if (activeTabId && !tabs.find((t) => t.id === activeTabId)) {
      activeTabId = tabs[0]?.id ?? null;
    }
    set({ tabs, activeTabId });
    writeStored(tenantId, { tabs, activeTabId });
  },

  newTab: (tenantId) => {
    const { tabs } = get();
    if (tabs.length >= MAX_TABS) return null;
    const tab: VoiceTab = {
      id: randomUUID(),
      label: `Voice tab ${tabs.length + 1}`,
      createdAt: Date.now(),
    };
    const nextTabs = [...tabs, tab];
    set({ tabs: nextTabs, activeTabId: tab.id });
    writeStored(tenantId, { tabs: nextTabs, activeTabId: tab.id });
    return tab;
  },

  closeTab: async (tenantId, tabId) => {
    const state = get();
    const idx = state.tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return;
    const nextTabs = state.tabs.filter((t) => t.id !== tabId);
    let nextActive = state.activeTabId;
    if (state.activeTabId === tabId) {
      // Pick next forward if it exists; otherwise previous; otherwise null.
      // Mirrors chatTabsStore.ts:157-159 selection rule.
      nextActive = nextTabs[idx]?.id ?? nextTabs[idx - 1]?.id ?? null;
    }
    set({ tabs: nextTabs, activeTabId: nextActive });
    writeStored(tenantId, { tabs: nextTabs, activeTabId: nextActive });
    // 1.167.0 — release the closed tab's byTabId entry in voiceStore so
    // its persisted history isn't kept under a now-orphaned tab id. The
    // voiceStore subscriber catches the byTabId change and writes the
    // cleaned history map. Hydrate has a defensive orphan cleanup but
    // doing it here is immediate (no wait until next reload).
    useVoiceStore.getState().dropTab(tabId);
  },

  setActiveTab: (tenantId, tabId) => {
    const { tabs } = get();
    if (!tabs.find((t) => t.id === tabId)) return;
    set({ activeTabId: tabId });
    writeStored(tenantId, { tabs, activeTabId: tabId });
  },

  updateLabel: (tenantId, tabId, label) => {
    const state = get();
    const idx = state.tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return;
    const nextTabs = state.tabs.map((t) =>
      t.id === tabId ? { ...t, label } : t,
    );
    set({ tabs: nextTabs });
    writeStored(tenantId, { tabs: nextTabs, activeTabId: state.activeTabId });
  },
}));
